<template>
    <main class="p-2 w-full h-full max-h-main flex flex-col items-start min-h-main">
        <div class="w-full">
            <div class="flex w-full">
                <app-button @click="addPreparation()" :label="$t('preparations.newPreparation')" v-if="!readOnly" />
            </div>
            <app-multi-picker
                ref="filter"
                icon="icon-magnify"
                :allowStringCriteria="true"
                class="w-full my-2"
                v-model="filterValue"
                :options="filterOptions"
                :strictMatching="true"
            >
                <template v-slot:option="{ option }">
                    <span>{{ option.fullName || option.name }}</span>
                    <span class="text-xs text-gray-600 ml-1">{{ option.criteriaType }}</span>
                </template>
            </app-multi-picker>

            <div class="flex justify-between">
                <div>
                    <app-select @input="onAction" v-if="!loading && selection.length > 0 && !readOnly" class="text-xs">
                        <option value="" disabled selected>{{ $t('commons.actions') }}</option>
                        <option value="modify">{{ $t('commons.actionModify') }}</option>
                        <option value="copyToBundles">{{ $t('administrativeFiles.copyToBundles') }}</option>
                        <option value="rejectAll">{{ $t('administrativeFiles.rejectAll') }}</option>
                        <option value="validateAll">{{ $t('administrativeFiles.validateAll') }}</option>
                        <option value="nonCompliantAll">{{ $t('administrativeFiles.nonCompliantAll') }}</option>
                        <option value="delete">{{ $t('commons.actionDelete') }}</option>
                    </app-select>
                </div>
                <div class="w-full flex flex-col justify-start gap-1 sm:gap-4 sm:justify-center sm:flex-row sm:">
                    <app-checkbox
                        class="justify-end sm:justify-center"
                        v-model="done"
                        :label="$t('project.follow.follow.done') + ' (' + doneCount + ')'"
                    ></app-checkbox>
                    <app-checkbox
                        class="justify-end sm:justify-center"
                        v-model="coming"
                        :label="$t('project.follow.follow.coming') + ' (' + comingCount + ')'"
                    ></app-checkbox>
                    <app-checkbox
                        class="justify-end sm:justify-center"
                        v-model="far"
                        :label="$t('project.follow.follow.far') + ' (' + farCount + ')'"
                    ></app-checkbox>
                </div>
            </div>
        </div>
        <div class="flex w-full flex-grow overflow-auto flex-col text-xs relative">
            <div v-if="loading" class="flex justify-center">
                <icon-rotate-right class="animate animate-spin"></icon-rotate-right>
            </div>
            <table class="table-fixed" v-else>
                <thead class="sticky top-0 bg-white">
                    <tr>
                        <th style="width: 2rem" class="hidden sm:table-cell text-left border-r p-1" v-if="!readOnly">
                            <app-checkbox
                                :value="selection.length === filteredItems.length && selection.length > 0"
                                :indeterminate="selection.length > 0 && selection.length < filteredItems.length"
                                :show-label="false"
                                :label="$t('commons.toggleSelectAll')"
                                @input="toggleSelectAll"
                            ></app-checkbox>
                        </th>
                        <th class="text-left border-r p-1">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('name')"
                            >
                                {{ $t('preparations.name') }}
                                <div v-if="sortKey === 'name'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="text-left border-r p-1" style="width: 15rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-between"
                                @click="sortBy('emitter')"
                            >
                                {{ $t('bundles.emitter') }}
                                <div v-if="sortKey === 'emitter'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell text-center border-r p-1">
                            <div :title="$t('commons.version')">
                                {{ $t('commons.versionShort') }}
                            </div>
                        </th>
                        <th class="text-center border-r p-1" style="width: 6rem">
                            <button
                                class="hover:underline font-bold w-full flex justify-center"
                                @click="sortBy('emissionDueDate')"
                            >
                                {{ $t('preparations.emissionDueDateShort') }}
                                <div v-if="sortKey === 'emissionDueDate'">
                                    <icon-menu-up v-if="sortAsc" width="16" height="16" />
                                    <icon-menu-down v-else width="16" height="16" />
                                </div>
                            </button>
                        </th>
                        <th class="hidden sm:table-cell text-center border-r p-1" style="width: 6rem">
                            {{ $t('preparations.emissionDateShort') }}
                        </th>
                        <th class="hidden sm:table-cell text-center border-r p-1" style="width: 6rem">
                            {{ $t('preparations.expirationDateShort') }}
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell border-r">
                            {{ $t('preparations.visaMOE') }}
                        </th>
                        <th style="width: 3rem" class="hidden sm:table-cell border-r">
                            {{ $t('preparations.visaMOA') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="preparation in filteredItems">
                        <tr class="odd:bg-blue-50 border-t">
                            <td class="hidden sm:table-cell border-r p-1" v-if="!readOnly">
                                <a :id="'uuid_' + preparation.id" style="scroll-margin-top: 3em"></a>
                                <app-checkbox
                                    :value="preparation.isSelected"
                                    :label="$t('commons.select')"
                                    :show-label="false"
                                    @input="saveSelection(preparation)"
                                ></app-checkbox>
                            </td>
                            <template>
                                <td class="border-r p-1">
                                    <router-link
                                        class="hover:underline"
                                        :to="{
                                            name: 'administrativeFile',
                                            params: {
                                                ...$route.params,
                                                preparationId: preparation.id,
                                            },
                                        }"
                                    >
                                        <span v-if="preparation.code" class="mr-1">#{{ preparation.code }}</span>
                                        <span class="font-bold">{{ preparation.name }}</span>
                                        <span v-if="!preparation.name || preparation.name.trim().length === 0">
                                            ...
                                        </span>
                                    </router-link>
                                </td>
                                <td class="text-left border-r p-1">
                                    <div
                                        :title="
                                            preparation.bundle && preparation.bundle.company
                                                ? preparation.bundle.company.name
                                                : ''
                                        "
                                    >
                                        {{ preparation.bundle ? preparation.bundle.label : '' }}
                                    </div>
                                </td>
                            </template>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <router-link
                                    class="hover:underline"
                                    :to="{
                                        name: 'administrativeFile',
                                        params: {
                                            ...$route.params,
                                            preparationId: preparation.id,
                                        },
                                    }"
                                >
                                    {{ preparation.versions.length }}
                                </router-link>
                            </td>
                            <td
                                class="text-center border-r p-1"
                                :class="{
                                    'text-red-600 font-bold':
                                        !preparation.emissionDate && preparation.emissionDueDate < new Date(),
                                }"
                            >
                                <div class="flex justify-center">
                                    <app-date-link
                                        @enter="focusToNextDueDate(preparation)"
                                        :ref="'dueDate_' + preparation.id"
                                        :disabled="readOnly"
                                        :label="$t('preparations.emissionDueDateShort')"
                                        :show-label="false"
                                        v-model="preparation.emissionDueDate"
                                        @input="updateDueDate(preparation, $event)"
                                    ></app-date-link>
                                </div>
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <div class="flex justify-center">
                                    <app-date-link
                                        @enter="focusToNextDate(preparation)"
                                        :ref="'date_' + preparation.id"
                                        :disabled="readOnly"
                                        :label="$t('preparations.emissionDateShort')"
                                        :show-label="false"
                                        v-model="preparation.emissionDate"
                                        @input="updateDate(preparation, $event)"
                                    ></app-date-link>
                                </div>
                            </td>
                            <td class="hidden sm:table-cell text-center border-r p-1">
                                <span
                                    v-if="preparation.expireAt"
                                    :class="'text-red-500' ? preparation.expireAt.getTime() < Date.now() : ''"
                                >
                                    {{ preparation.expireAt | humanizeDate }}
                                </span>
                                <span v-else></span>
                            </td>
                            <td v-for="reviewer in reviewers" style="width: 3rem" class="hidden sm:table-cell border-r">
                                <div class="flex justify-center" v-if="preparation.lastVisaByEmitterIdMap[reviewer.id]">
                                    <div
                                        class="flex justify-start"
                                        :title="$t('administrativeFiles.legend.documentToEmit')"
                                        v-if="!preparation.emissionDate"
                                    >
                                        <icon-pause-circle-outline class="text-gray-400" />
                                    </div>
                                    <div
                                        class="flex justify-start"
                                        :title="$t('administrativeFiles.legend.expiredTips')"
                                        v-else-if="preparation.expireAt && preparation.expireAt.getTime() < Date.now()"
                                    >
                                        <icon-clock-alert-outline class="text-red-700" />
                                    </div>
                                    <app-preparation-visa-status
                                        v-else
                                        :preparationEmissionDate="preparation.emissionDate"
                                        :visa="preparation.lastVisaByEmitterIdMap[reviewer.id]"
                                    ></app-preparation-visa-status>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
        <app-quick-actions :options="quickActions" @choose="$event.run()"></app-quick-actions>
        <app-popup ref="modifyAllPopup" :showHeader="true" :title="$t('commons.actionOnSelection')">
            <ValidationObserver
                v-slot="{ invalid, errors, dirty }"
                tag="form"
                ref="observer"
                class="p-2 gap-2 flex flex-col"
            >
                <app-bundle-picker v-model="editedPreparation.bundle" :options="bundles"></app-bundle-picker>
                <app-date-input
                    v-model="editedPreparation.emissionDueDate"
                    :label="$t('preparations.emissionDueDate')"
                ></app-date-input>
                <app-date-input v-model="editedPreparation.emissionDate" :label="$t('preparations.emissionDate')" />
                <app-footer @click="saveMultiple" :disabled="invalid" class="mt-2"></app-footer>
            </ValidationObserver>
        </app-popup>
        <app-popup ref="importPreparationsPopup" :showHeader="true" :title="$t('preparations.importPreparations')">
            <app-textarea
                ref="pasteArea"
                :placeholder="$t('preparations.importPreparationsTips')"
                @paste="onPaste"
            ></app-textarea>
        </app-popup>
        <app-multi-picker
            ref="multiBundlePicker"
            class="col-span-2"
            variant="hidden"
            :show-label="false"
            v-model="bundlesToCopy"
            :options="bundles"
            :title="$t('preparations.copyByBundles')"
        >
            <template v-slot:option="{ option }">
                <app-bundle :bundle="option"></app-bundle>
            </template>
            <template v-slot:footer>
                <div class="p-4 flex justify-end">
                    <app-button
                        @click="copyToBundles"
                        :disabled="bundlesToCopy.length === 0"
                        variant="primary"
                        :label="$t('preparations.copyByBundleValidation')"
                    ></app-button>
                </div>
            </template>
        </app-multi-picker>
    </main>
</template>

<script>
import AppCheckbox from '../../components/app-checkbox/AppCheckbox';
import { sortBy, filterMatch, get, uniqBy, getMapById } from '@/services/sanitize.service';
import { differenceInWorkingDays } from '@/services/duration.service';
import AppList from '../../components/appList/AppList';
import AppSelect from '@/components/appSelect/AppSelect';
import AppMultiPicker from '@/components/appMultiPicker/AppMultiPicker';
import AppQuickActions from '@/components/appQuickActions/AppQuickActions';
import AppButton from '@/components/appButton/AppButton';
import { removePreparationGroup, updatePreparation } from '@/features/preparations/preparations.service';
import AppPreparationVisaStatus from '@/components/appPreparationVisaStatus/AppPreparationVisaStatus';
import AppDateLink from '@/components/appDateLink/AppDateLink';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppPopup from '@/components/app-popup/AppPopup';
import AppBundlePicker from '@/components/appBundlePicker';
import AppDateInput from '@/components/appDateInput/AppDateInput';
import AppFooter from '@/components/appFooter/AppFooter';
import AppInputText from '@/components/appInputText/AppInputText';
import AppCancel from '@/components/appCancel/AppCancel';
import IconMenuDown from '@/icons/IconMenuDown';
import IconMenuUp from '@/icons/IconMenuUp';
import AppNumberInput from '@/components/appNumberInput/AppNumberInput';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink';
import { queryProject } from '@/features/projects/projects.service';
import { getCalendar } from '@/features/planning/agenda/agenda.service';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getPreparations } from '@/features/preparations/preparations.service';
import { getCompanies } from '@/features/companies/companies.service';
import { getPlannedTasks } from '@/features/tasks/plannedTasks.service';
import AppTips from '@/components/app-tips/AppTips.vue';
import { importPreparations } from './pasteAdministrativeFiles.service';
import AppTextarea from '@/components/app-textarea/AppTextarea.vue';
import { updatePreparationVisa } from '@/features/preparations/preparationVisas.service';
import IconPauseCircleOutline from '@/icons/IconPauseCircleOutline.vue';
import IconClockAlertOutline from '@/icons/IconClockAlertOutline.vue';
import AppBundle from '@/components/app-bundle/appBundle.vue';
import { newAdministrativeFile } from '@/features/administrativeFiles/AdministrativeFiles.service';

export default {
    components: {
        AppBundle,
        IconClockAlertOutline,
        IconPauseCircleOutline,
        AppTextarea,
        AppTips,
        AppNumberLink,
        AppNumberInput,
        IconMenuUp,
        IconMenuDown,
        AppCancel,
        AppInputText,
        AppFooter,
        AppDateInput,
        AppBundlePicker,
        AppPopup,
        AppDateLink,
        AppPreparationVisaStatus,
        AppButton,
        AppQuickActions,
        AppMultiPicker,
        AppSelect,
        AppList,
        AppCheckbox,
    },
    async created() {
        this.restoreFilter();
        queryProject(this.$route.params.projectId).then((project) => {
            this.preparationReviewMargin = project.preparationReviewMargin;
            this.readOnly = !project.me.allowedFeatures.includes('project_administrativeFiles');
        });
        this.init();
    },
    computed: {
        filteredItems() {
            let result = uniqBy(this.filterFn(this.filterValue), 'groupId').map((item) => ({
                ...item,
                isSelected: this.selection.includes(item.id),
            }));
            if (this.sortKey) {
                result = sortBy(result, (item) => {
                    if (this.sortKey === 'name') {
                        return item.code ? item.code + ' ' + item.name : item.name;
                    } else if (this.sortKey === 'type') {
                        return item.type ? this.$t('preparations.types.' + item.type) : null;
                    } else if (this.sortKey === 'emissionDueDate') {
                        return item.emissionDueDate;
                    } else if (this.sortKey === 'emitter') {
                        return item.bundle ? item.bundle.label : null;
                    }
                });
                if (!this.sortAsc) {
                    result.reverse();
                }
            }
            return result;
        },
        filterOptions() {
            return [
                {
                    isGroup: true,
                    name: this.$t('commons.status'),
                    id: this.$t('commons.status'),
                    children: [
                        {
                            name: this.$t('preparations.status.toEmit'),
                            id: 'preparations.status.toEmit',
                            firstOfCriteriaType: true,
                            _isStateCriteria: true,
                        },
                        {
                            name: this.$t('preparations.status.toVisa'),
                            id: 'preparations.status.toVisa',
                            firstOfCriteriaType: false,
                            _isStateCriteria: true,
                        },
                    ],
                },
                {
                    isGroup: true,
                    name: this.$t('commons.emitterCriteriaType'),
                    id: this.$t('commons.emitterCriteriaType'),
                    children: this.bundles.map((bundle, index) => ({
                        ...bundle,
                        name: bundle.label,
                        firstOfCriteriaType: index === 0,
                        criteriaType: this.$t('commons.emitterCriteriaType'),
                        _isBundleCriteria: true,
                    })),
                },
            ];
        },
        reviewers() {
            const visaEmittersMap = {};
            const emitterListWithDup = this.filteredItems.reduce(
                (acc, preparation) => [
                    ...acc,
                    ...preparation.visas.reduce((acc, visa) => (visa.emitterId ? [...acc, visa.emitterId] : acc), []),
                ],
                [],
            );
            for (const emitterId of emitterListWithDup) {
                if (visaEmittersMap[emitterId]) {
                    visaEmittersMap[emitterId].score = visaEmittersMap[emitterId].score + 1;
                } else {
                    visaEmittersMap[emitterId] = { emitterId, score: 0 };
                }
            }
            return sortBy(Object.values(visaEmittersMap), 'score')
                .reverse()
                .map((entity) => this.bundleMap[entity.emitterId])
                .filter((a) => !!a);
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                getCalendar(this.$route.params.projectId).subscribe((agenda) => (this.agenda = agenda)),
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getPreparations(this.$route.params.projectId, 'administrative'),
                    getCompanies(this.$route.params.projectId),
                    getPlannedTasks(this.$route.params.projectId, new BehaviorSubject(new Date())),
                ]).subscribe(([bundles, preparations, companies, planning]) => {
                    this.companies = companies.map((company) => ({
                        ...company,
                        bundleIds: bundles
                            .filter((bundle) => bundle.companyId === company.id)
                            .map((bundle) => bundle.id),
                    }));
                    const companyMap = getMapById(companies);
                    this.bundles = sortBy(
                        bundles.map((bundle) => ({ ...bundle, company: companyMap[bundle.companyId] })),
                        (bundle) => bundle.label,
                    );
                    this.bundleMap = getMapById(this.bundles);
                    this.restoreSelection();
                    this.items = preparations.map((preparation) => {
                        const bundle = this.bundleMap[preparation.bundleId];
                        return {
                            ...preparation,
                            lastVisaByEmitterIdMap: preparation.visas.reduce(
                                (acc, visa) => ({ ...acc, [visa.emitterId]: visa }),
                                {},
                            ),
                            versions: preparation.versions.map((version) => ({
                                ...version,
                                bundle: this.bundleMap[version.bundleId],
                                lastVisaByEmitterIdMap: version.visas.reduce(
                                    (acc, visa) => ({ ...acc, [visa.emitterId]: visa }),
                                    {},
                                ),
                            })),
                            bundle,
                            filterString: [
                                preparation.code,
                                preparation.name,
                                preparation.bundleId && bundle ? bundle.label : '',
                            ].join(),
                            isSelected: this.selection.includes(preparation.id),
                            taskPredecessors: preparation.predecessors
                                .map((predecessor) => planning.find((aTask) => aTask.id === predecessor.taskId))
                                .filter((a) => !!a),
                        };
                    });
                    this.doneCount = this.items.filter((item) => this.isDone(item)).length;
                    this.farCount = this.items.filter((item) => this.isFar(item)).length;
                    this.comingCount = this.items.filter((item) => !this.isDone(item) && !this.isFar(item)).length;
                    this.cleanupSavedSelection();
                    this.scrollToLastVisited();
                    this.loading = false;
                }),
            ];
        },
        scrollToLastVisited() {
            const lastVisitedPreparationId = localStorage.getItem(
                'administrativeFiles.lastVisitedPreparationId.' + this.$route.params.projectId,
            );
            if (lastVisitedPreparationId) {
                setTimeout(() => {
                    const element = this.$el.querySelector('#uuid_' + lastVisitedPreparationId);
                    if (element) element.scrollIntoView();
                }, 800);
            }
        },
        async onPaste(e) {
            if (this.$refs.pasteArea.$el.contains(e.target)) {
                const preparations = importPreparations(e, this.$route.params.projectId, this.bundles);
                this.$refs.importPreparationsPopup.close();
                preparations.map(async (preparation) =>
                    newAdministrativeFile(this.$route.params.projectId, this.bundles, preparation, []),
                );
            }
        },
        get,
        sortBy(key) {
            if (key === this.sortKey) {
                this.sortAsc = !this.sortAsc;
            } else {
                this.sortKey = key;
                this.sortAsc = true;
            }
            this.saveFilter(this.filterValue);
        },
        saveMultiple() {
            this.selection.map((itemId) => {
                const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
                const patch = {};
                if (this.editedPreparation.emissionDueDate) {
                    patch.emissionDueDate = this.editedPreparation.emissionDueDate;
                } else if (this.getSelectionCommonEmissionDueDate(selectedItems)) {
                    patch.emissionDueDate = null;
                }
                if (this.editedPreparation.emissionDate) {
                    patch.emissionDate = this.editedPreparation.emissionDate;
                } else if (this.getSelectionCommonEmissionDate(selectedItems)) {
                    patch.emissionDate = null;
                }
                if (this.editedPreparation.bundle) {
                    patch.bundleId = this.editedPreparation.bundle.id;
                } else if (this.getSelectionCommonBundle(selectedItems)) {
                    patch.bundleId = null;
                }
                return updatePreparation(this.$route.params.projectId, { id: itemId, ...patch });
            });
            this.$refs.modifyAllPopup.close();
        },
        async onAction(action) {
            const selectedItems = this.items.filter((item) => this.selection.includes(item.id));
            if (action === 'delete') {
                if (await confirm(this.$t('commons.confirmMessageAll', { number: this.selection.length }))) {
                    await Promise.all(
                        selectedItems.map((item) => removePreparationGroup(this.$route.params.projectId, item.groupId)),
                    );
                    this.selection = [];
                }
            } else if (action === 'modify') {
                this.editedPreparation = {
                    emissionDueDate: this.getSelectionCommonEmissionDueDate(selectedItems),
                    emissionDate: this.getSelectionCommonEmissionDate(selectedItems),
                    bundle: this.getSelectionCommonBundle(selectedItems),
                };
                this.$refs.modifyAllPopup.open();
            } else if (action === 'copyToBundles') {
                this.$refs.multiBundlePicker.open();
            } else if (action === 'rejectAll') {
                this.rejectAll();
            } else if (action === 'validateAll') {
                this.validateAll();
            } else if (action === 'nonCompliantAll') {
                this.nonCompliantAll();
            }
        },
        async copyToBundles() {
            this.$refs.multiBundlePicker.close();
            await Promise.all(
                this.items
                    .filter((preparation) => preparation.isSelected)
                    .map((preparationModel) => {
                        return Promise.all(
                            this.bundlesToCopy.map((bundle) => {
                                return newAdministrativeFile(
                                    this.$route.params.projectId,
                                    this.bundles,
                                    {
                                        name: preparationModel.name,
                                        code: preparationModel.code,
                                        phase: preparationModel.phase,
                                        bundleId: bundle.id,
                                        emissionDueDate: preparationModel.emissionDueDate,
                                    },
                                    [],
                                );
                            }),
                        );
                    }),
            );
            this.bundlesToCopy = [];
        },
        getSelectionCommonBundle(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.bundle);
            if (firstItem && selectedItems.every((item) => item.bundle && item.bundle.id === firstItem.bundle.id)) {
                return firstItem.bundle;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) => item.emissionDate && item.emissionDate.getTime() === firstItem.emissionDate.getTime(),
                )
            ) {
                return firstItem.emissionDate;
            } else {
                return null;
            }
        },
        getSelectionCommonEmissionDueDate(selectedItems) {
            const firstItem = selectedItems.find((item) => !!item.emissionDueDate);
            if (
                firstItem &&
                selectedItems.every(
                    (item) =>
                        item.emissionDueDate && item.emissionDueDate.getTime() === firstItem.emissionDueDate.getTime(),
                )
            ) {
                return firstItem.emissionDueDate;
            } else {
                return null;
            }
        },
        toggleSelectAll() {
            if (this.selection.length < this.filteredItems.length) {
                this.selection = this.filteredItems.map((item) => item.id);
            } else {
                this.selection = [];
            }
            this.saveSelection();
        },
        cleanupSavedSelection() {
            this.selection = this.selection.filter((itemId) => !!this.items.find((anItem) => anItem.id === itemId));
        },
        saveSelection(item) {
            if (item) {
                if (!item.isSelected) {
                    this.selection.push(item.id);
                } else {
                    this.selection = this.selection.filter((anItem) => anItem !== item.id);
                }
            }
            localStorage.setItem(
                'administrativeFiles.selection.' + this.$route.params.projectId,
                JSON.stringify(this.selection),
            );
        },
        restoreSelection() {
            const cache = localStorage.getItem('administrativeFiles.selection.' + this.$route.params.projectId);
            if (cache) {
                this.selection = JSON.parse(cache);
            }
        },
        concludeAll(conclusion) {
            return Promise.all(
                this.items
                    .filter((preparation) => preparation.isSelected)
                    .map((preparation) => {
                        const visa = preparation.visas[0];
                        return updatePreparationVisa(this.$route.params.projectId, {
                            id: visa.id,
                            emissionDate: visa.emissionDate || new Date(),
                            conclusion,
                        });
                    }),
            );
        },
        rejectAll() {
            return this.concludeAll('rejected');
        },
        validateAll() {
            return this.concludeAll('approved');
        },
        nonCompliantAll() {
            return this.concludeAll('nonCompliant');
        },
        async addPreparation() {
            const result = await newAdministrativeFile(
                this.$route.params.projectId,
                this.bundles,
                { name: this.$t('preparations.newName') },
                [],
            );
            await this.$router.push({
                name: 'administrativeFile',
                params: {
                    projectId: this.$route.params.projectId,
                    preparationId: result.id,
                },
            });
        },
        isDone(item) {
            return (
                item.emissionDate &&
                (!item.expireAt || item.expireAt.getTime() > Date.now()) &&
                item.visas.every(
                    (visa) =>
                        visa.emissionDate &&
                        (visa.conclusion === 'approved' || visa.conclusion === 'approvedWithComments'),
                )
            );
        },
        isFar(item) {
            const diff = differenceInWorkingDays(
                item.emissionDueDate,
                new Date(),
                this.agenda,
                item.bundle ? item.bundle.id : null,
            );
            return !item.emissionDate && diff > 5;
        },
        matchBundleFilter(item, bundleIds) {
            return (
                (item.bundle && bundleIds.includes(item.bundle.id)) ||
                item.visas.find((visa) => visa.emitter && bundleIds.includes(visa.emitter.id))
            );
        },
        matchString(stringCriteria, item) {
            if (!stringCriteria || stringCriteria.length === 0) {
                return true;
            }
            return stringCriteria.find((criteria) => filterMatch(item.filterString || item.name, criteria, true));
        },
        matchStatusFilter(item) {
            const isFar = this.isFar(item);
            const isDone = this.isDone(item);
            return (!isDone && !isFar && this.coming) || (isFar && this.far) || (isDone && this.done);
        },
        matchVisaEmitterFilter(item, bundleIds) {
            return item.visas.find(({ emitterId }) => bundleIds.includes(emitterId));
        },
        saveFilter(filterValue) {
            localStorage.setItem(
                'administrativeFiles_filter_' + this.$route.params.projectId,
                JSON.stringify({
                    filterValue,
                    done: this.done,
                    coming: this.coming,
                    far: this.far,
                    sortKey: this.sortKey,
                }),
            );
        },
        restoreFilter() {
            const cache = localStorage.getItem('administrativeFiles_filter_' + this.$route.params.projectId);
            if (cache) {
                const parsedCache = JSON.parse(cache);
                this.filterValue = parsedCache.filterValue || [];
                this.done = !!parsedCache.done;
                this.coming = !!parsedCache.coming;
                this.far = !!parsedCache.far;
                this.sortKey = parsedCache.sortKey || 'name';
            }
        },
        filterFn(filter) {
            this.saveFilter(filter);
            const bundleIds = [
                ...filter.filter((aCriteria) => aCriteria._isBundleCriteria).map((bundle) => bundle.id),
                ...filter
                    .filter((aCriteria) => aCriteria._isCompanyCriteria)
                    .reduce((acc, company) => [...acc, ...(company.bundleIds || [])], []),
            ];
            const stringCriteria = filter
                .filter((aCriteria) => aCriteria._isStringCriteria)
                .map((aCriteria) => aCriteria.content);
            const toEmitCriteria = filter.filter((aCriteria) => aCriteria.id === 'preparations.status.toEmit');
            const toVisaCriteria = filter.filter((aCriteria) => aCriteria.id === 'preparations.status.toVisa');

            return this.items.filter((item) => {
                const fullCriteria = {
                    matchBundleFilter: bundleIds.length === 0 || this.matchBundleFilter(item, bundleIds),
                    matchStatusFilter: this.matchStatusFilter(item),
                    matchString: this.matchString(stringCriteria, item),
                    matchToEmit: toEmitCriteria.length === 0 || !item.emissionDate,
                    matchToVisa: toVisaCriteria.length === 0 || (!!item.emissionDate && !this.isDone(item)),
                };
                const filterResult = Object.values(fullCriteria).every((value) => !!value);
                if (!filterResult) {
                    this.selection = this.selection.filter((id) => id !== item.id);
                }
                return filterResult;
            });
        },
        updateDueDate(preparation, date) {
            updatePreparation(this.$route.params.projectId, { emissionDueDate: date, id: preparation.id });
        },
        updateDate(preparation, date) {
            if (preparation.emissionDueDate) {
                updatePreparation(this.$route.params.projectId, {
                    emissionDate: date,
                    id: preparation.id,
                });
            } else {
                updatePreparation(this.$route.params.projectId, {
                    id: preparation.id,
                    emissionDueDate: date,
                    emissionDate: date,
                });
            }
        },
        focusToNextDate(preparation) {
            let index = this.filteredItems.findIndex((aPreparation) => aPreparation.id === preparation.id);
            let nextPreparation = this.filteredItems[index + 1];
            while (!nextPreparation && index < this.filteredItems.length) {
                nextPreparation = this.filteredItems[++index];
            }
            if (nextPreparation) {
                this.$emit('select', nextPreparation);
                const element = this.$refs['date_' + nextPreparation.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
        focusToNextDueDate(preparation) {
            let index = this.filteredItems.findIndex((aPreparation) => aPreparation.id === preparation.id);
            let nextPreparation = this.filteredItems[index + 1];
            while (!nextPreparation && index < this.filteredItems.length) {
                nextPreparation = this.filteredItems[++index];
            }
            if (nextPreparation) {
                this.$emit('select', nextPreparation);
                const element = this.$refs['dueDate_' + nextPreparation.id];
                if (element) {
                    if (Array.isArray(element)) {
                        element[0].focus();
                    } else {
                        element.focus();
                    }
                }
            }
        },
    },
    data() {
        return {
            bundleMap: {},
            preparationReviewMargin: 0,
            loading: 0,
            doneCount: 0,
            farCount: 0,
            comingCount: 0,
            quickActions: [
                {
                    name: this.$t('preparations.newPreparation'),
                    run: () => this.addPreparation(),
                },
                {
                    name: this.$t('preparations.importPreparations'),
                    run: () => this.$refs.importPreparationsPopup.open(),
                },
            ],
            sortKey: 'name',
            sortAsc: true,
            readOnly: true,
            selection: [],
            editedPreparation: {
                emissionDueDate: null,
                emissionDate: null,
                bundle: null,
            },
            subscriptions: [],
            bundlesToCopy: [],
            agenda: [],
            bundles: [],
            companies: [],
            items: [],
            done: false,
            coming: true,
            far: false,
            filterValue: [],
        };
    },
};
</script>
